import { useCallback, useEffect, useState } from "react";
import { Paperclip } from "lucide-react";
import { useAuth } from "@/context/auth";
import { useI18n } from "@/i18n";
import { Btn, Badge, Spinner, fmtDate } from "@/components/ui-rp";

export const EvidenceList = ({ evidence, urlFor, client, t }) => {
  const view = async (e) => { const { data } = await client.get(urlFor(e), { responseType: "blob" }); window.open(URL.createObjectURL(data), "_blank"); };
  if (!evidence?.length) return <p className="text-xs text-muted-foreground" data-testid="dispute-no-evidence">{t("dispute.none")}</p>;
  return (
    <ul className="space-y-1 text-xs" data-testid="dispute-evidence-list">
      {evidence.map((e) => (
        <li key={e.id} className="flex justify-between gap-2" data-testid={`evidence-${e.id}`}>
          <span className="truncate flex items-center gap-1"><Paperclip className="w-3 h-3 shrink-0" />{e.filename} <span className="text-muted-foreground">· {(e.size / 1024).toFixed(0)} KB</span></span>
          <button type="button" onClick={() => view(e)} className="underline shrink-0" data-testid={`evidence-view-${e.id}`}>{t("dispute.download")}</button>
        </li>
      ))}
    </ul>
  );
};

export const DisputeTimeline = ({ events, lang, t }) => (
  <ul className="space-y-1 text-xs" data-testid="dispute-timeline">
    {(events || []).map((e, i) => (
      <li key={i} className="flex justify-between gap-3"><span className="font-medium">{t(`status.${e.to}`)}{e.meta?.evidence_added ? ` (+${e.meta.evidence_added})` : ""} <span className="text-muted-foreground">· {e.actor}</span></span><span className="text-muted-foreground font-mono">{fmtDate(e.at, lang)}</span></li>
    ))}
  </ul>
);

const FilesInput = ({ files, setFiles, t, testId }) => (
  <label className="block">
    <span className="block text-sm font-medium mb-1.5">{t("dispute.files")}</span>
    <input data-testid={testId} type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.webp,application/pdf,image/*" onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 5))}
      className="block w-full text-sm file:mr-3 file:rounded-full file:border-0 file:bg-secondary file:px-4 file:py-2 file:text-sm file:font-semibold" />
    {files.length > 0 && <span className="block text-xs text-muted-foreground mt-1">{files.map((f) => f.name).join(", ")}</span>}
  </label>
);

export const DisputeSection = ({ order, onChanged }) => {
  const { t, lang } = useI18n();
  const { client, user } = useAuth();
  const [data, setData] = useState(null);
  const [reason, setReason] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const load = useCallback(() => client.get(`/tenant/orders/${order.id}/dispute`).then(({ data }) => setData(data)).catch(() => setData({ dispute: null, can_open: false })), [client, order.id]);
  useEffect(() => { load(); }, [load]);
  const send = async (url, extra) => {
    setBusy(true); setMsg("");
    const fd = new FormData();
    if (extra?.reason !== undefined) fd.append("reason", extra.reason);
    files.forEach((f) => fd.append("files", f));
    try { await client.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } }); setMsg(t(extra ? "dispute.opened" : "dispute.evidenceAdded")); setFiles([]); load(); onChanged?.(); }
    catch (e) { setMsg(e.response?.data?.detail?.message || t("common.error")); }
    finally { setBusy(false); }
  };
  if (!data) return <Spinner />;
  const d = data.dispute;
  const isAdmin = user.role === "admin";
  return (
    <div className="rounded-xl border border-border p-4 space-y-3" data-testid="dispute-section">
      <div className="flex items-center justify-between"><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{t("dispute.title")}</p>{d && <Badge status={d.status} label={t(`status.${d.status}`)} testId="dispute-status" />}</div>
      {!d && data.deadline_at && <p className="text-xs text-muted-foreground" data-testid="dispute-deadline">{data.can_open ? t("dispute.deadline", { date: fmtDate(data.deadline_at, lang) }) : t("dispute.expired")}</p>}
      {!d && data.can_open && isAdmin && (
        <form onSubmit={(e) => { e.preventDefault(); send(`/tenant/orders/${order.id}/dispute`, { reason }); }} className="space-y-3" data-testid="dispute-form">
          <label className="block"><span className="block text-sm font-medium mb-1.5">{t("dispute.reason")}</span>
            <textarea data-testid="dispute-reason" required minLength={10} rows={3} value={reason} onChange={(e) => setReason(e.target.value)} className="w-full rounded-xl border border-border bg-background px-3 py-2 text-sm" />
            <span className="block text-xs text-muted-foreground mt-1">{t("dispute.reasonHint")}</span></label>
          <FilesInput files={files} setFiles={setFiles} t={t} testId="dispute-files" />
          <Btn type="submit" busy={busy} testId="dispute-submit" className="h-10">{t("dispute.submit")}</Btn>
        </form>
      )}
      {d && (
        <>
          <p className="text-sm whitespace-pre-wrap" data-testid="dispute-reason-text">{d.reason}</p>
          <div><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">{t("dispute.evidence")}</p>
            <EvidenceList evidence={d.evidence} urlFor={(e) => `/tenant/disputes/${d.id}/evidence/${e.id}`} client={client} t={t} /></div>
          {d.resolution_note && <p className="text-sm" data-testid="dispute-resolution-note"><span className="text-muted-foreground">{t("dispute.note")}:</span> {d.resolution_note}</p>}
          <div><p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1">{t("dispute.timeline")}</p><DisputeTimeline events={d.events} lang={lang} t={t} /></div>
          {isAdmin && ["open", "under_review"].includes(d.status) && d.evidence.length < 5 && (
            <form onSubmit={(e) => { e.preventDefault(); send(`/tenant/disputes/${d.id}/evidence`); }} className="space-y-2" data-testid="dispute-evidence-form">
              <FilesInput files={files} setFiles={setFiles} t={t} testId="dispute-more-files" />
              <Btn type="submit" variant="outline" busy={busy} disabled={files.length === 0} testId="dispute-add-evidence" className="h-9">{t("dispute.addEvidence")}</Btn>
            </form>
          )}
        </>
      )}
      {msg && <p className={`text-sm ${/enviad|agregad|submitted|added/i.test(msg) ? "text-[hsl(122,87%,30%)] dark:text-primary" : "text-destructive"}`} data-testid="dispute-msg">{msg}</p>}
    </div>
  );
};
